import React, { useEffect, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { useFormik } from 'formik';
import { useMutation } from 'react-query';
import { _S3ImageResponse, _ToastMessageProps } from '../../../../../constants/staticTypes';
import { Calendar } from 'primereact/calendar';
import dayjs from 'dayjs';
import {
    ADD_USER,
    CANCEL,
    DELETE,
    DISPLAY_NAME,
    EDIT,
    EDIT_STAFF,
    EMAIL,
    FIRST_NAME,
    HIRE_DATE,
    LAST_NAME,
    NO_ORGANIZATIONS_ADDED,
    ORGANIZATION,
    ORGANIZATIONS,
    PASSWORD,
    PASSWORD_CHECK_LOWERCASE,
    PASSWORD_CHECK_MIN,
    PASSWORD_CHECK_NUMBER,
    PASSWORD_CHECK_SPECIAL,
    PASSWORD_CHECK_UPPERCASE,
    PERMISSION_GROUP,
    PHONE,
    POS,
    ROLES,
    SAVE,
    SEND_PASSWORD_LINK,
    UPDATE,
    USERNAME,
} from '../../../../../constants/strings';
import HttpServiceHelper from '../../../../../helpers/httpServiceHelper';
import { useTranslation } from '../../../../../hooks/useTranslation';
import { defaultToastData } from '../../../../../helpers/commonHelper';
import { AlertVariant, IMAGE_TYPE } from '../../../../../constants/constants';
import MlxButton from '../../../../../components/common/MlxButton/MlxButton';
import MlxDropDown from '../../../../../components/common/MlxDropDown/MlxDropDown';
import MlxImageUpload from '../../../../../components/common/MlxImageUpload/MlxImageUpload';
import MlxSideBar from '../../../../../components/common/MlxSideBar/MlxSideBar';
import MlxToast from '../../../../../components/common/MlxToast/MlxToast';
import { checkPasswordCriteria } from '../../../../../helpers/registerAccountHelper';
import {
    _AddUserVenueFormType,
    _VenueUserData,
    addUserVenueValidationSchema,
    addVenueUser,
    editVenueUser,
    getAddVenueUserApiFormattedData,
    initialAddUserVenueDataValues,
} from '../../../../../helpers/venueUsersHelper';
import MlxMultiSelect from '../../../../../components/common/MlxMultiSelect/MlxMultiSelect';
import MlxCountryPhoneInput from '../../../../../components/common/MlxCountryPhoneInput/MlxCountryPhoneInput';

type Props = {
    showSideBar: boolean;
    setShowSideBar: React.Dispatch<React.SetStateAction<boolean>>;
    setVenueUserAdded?: React.Dispatch<React.SetStateAction<boolean>>;
    isVenueUserEdit?: boolean;
    setIsVenueUserEdit?: React.Dispatch<React.SetStateAction<boolean>>;
    venueUserEditData?: _AddUserVenueFormType;
    selectedVenueOption?: string;
    venuePermissionGroups: any[];
    venuePermissionGroupsLoading: boolean;
    venueDefaultPermissionGroup: any;
    venueJobRoles: any[];
    venueJobRolesLoading: boolean;
};

const languageTexts = {
    POS: POS,
    ROLES: ROLES,
    ADD_STAFF: ADD_USER,
    EDIT_STAFF: EDIT_STAFF,
    SEND_PASSWORD_LINK: SEND_PASSWORD_LINK,
    USERNAME: USERNAME,
    PASSWORD: PASSWORD,
    DISPLAY_NAME: DISPLAY_NAME,
    FIRST_NAME: FIRST_NAME,
    LAST_NAME: LAST_NAME,
    EMAIL: EMAIL,
    PHONE: PHONE,
    HIRE_DATE: HIRE_DATE,
    PERMISSION_GROUP: PERMISSION_GROUP,
    ORGANIZATION: ORGANIZATION,
    ORGANIZATIONS: ORGANIZATIONS,
    SAVE: SAVE,
    EDIT: EDIT,
    DELETE: DELETE,
    CANCEL: CANCEL,
    UPDATE: UPDATE,
    NO_ORGANIZATIONS_ADDED: NO_ORGANIZATIONS_ADDED,
    PASSWORD_CHECK_LOWERCASE: PASSWORD_CHECK_LOWERCASE,
    PASSWORD_CHECK_UPPERCASE: PASSWORD_CHECK_UPPERCASE,
    PASSWORD_CHECK_NUMBER: PASSWORD_CHECK_NUMBER,
    PASSWORD_CHECK_MIN: PASSWORD_CHECK_MIN,
    PASSWORD_CHECK_SPECIAL: PASSWORD_CHECK_SPECIAL,
};

const AddVenueUserSideBar = ({
    selectedVenueOption,
    showSideBar,
    setShowSideBar,
    setVenueUserAdded,
    isVenueUserEdit,
    setIsVenueUserEdit,
    venueUserEditData,
    venuePermissionGroups,
    venuePermissionGroupsLoading,
    venueDefaultPermissionGroup,
    venueJobRoles,
    venueJobRolesLoading,
}: Readonly<Props>) => {
    const addOrgStaffMutation = useMutation(HttpServiceHelper);
    const translatedText = useTranslation(languageTexts, 'capitalize');
    const [sendPasswordLink, setSendPasswordLink] = useState<boolean>(true);
    const [toastMessage, setToastMessage] = useState<_ToastMessageProps>(defaultToastData);

    const onSuccess = (data: any) => {
        setToastMessage({
            message: data?.message ?? '',
            variant: AlertVariant.SUCCESS,
            show: true,
        });
        if (setVenueUserAdded) {
            setVenueUserAdded(true);
        }
        onSidebarClose();
    };

    const onError = (error: string, variant: string) => {
        setToastMessage({
            message: error,
            variant,
            show: true,
        });
        onSidebarClose();
    };

    // Formik setup
    const formik = useFormik({
        initialValues: initialAddUserVenueDataValues,
        enableReinitialize: true,
        validationSchema: addUserVenueValidationSchema,
        onSubmit: (values) => {
            const apiFormattedData: _VenueUserData = getAddVenueUserApiFormattedData(values);
            if (isVenueUserEdit) {
                // for edit staff, remove the loginUserName and email ids from the apiFormattedData
                // delete (apiFormattedData as any).loginUserName;
                // delete (apiFormattedData as any).email;
                editVenueUser(addOrgStaffMutation, apiFormattedData, onSuccess, onError, selectedVenueOption);
            } else {
                addVenueUser(addOrgStaffMutation, apiFormattedData, onSuccess, onError, selectedVenueOption);
            }
        },
    });

    useEffect(() => {
        formik.setFieldValue('staffPermissionRoles', venueDefaultPermissionGroup.id);
    }, [venueDefaultPermissionGroup]);

    useEffect(() => {
        if (venueUserEditData) {
            formik.setValues(venueUserEditData);
            setProfileImage({
                imageKey: venueUserEditData.photoKey,
                imageUrl: venueUserEditData.signedUrl ? venueUserEditData.signedUrl : null,
            });
        }
    }, [venueUserEditData]);

    const profileImageInitialValue = {
        imageKey: '',
        imageUrl: null, // need to get the image url using key
    };
    const [profileImage, setProfileImage] = useState<_S3ImageResponse>(profileImageInitialValue);

    useEffect(() => {
        formik.setFieldValue('photoKey', profileImage.imageKey);
    }, [profileImage]);

    const onSidebarClose = () => {
        setShowSideBar(false);
        setProfileImage(profileImageInitialValue);
        formik.resetForm();
        formik.setFieldValue('staffPermissionRoles', venueDefaultPermissionGroup.id);
        if (setIsVenueUserEdit) setIsVenueUserEdit(false);
        setSendPasswordLink(true);
    };

    const passwordCriteria = checkPasswordCriteria(formik.values.password);
    const PasswordRequirement = ({ className, text, isValid }: { className: string; text: string; isValid: boolean }) => {
        let styleClass = 'text-secondary';

        if (formik?.touched?.password && formik?.errors?.password && !isValid) {
            styleClass = 'text-danger';
        }

        if (isValid) {
            styleClass = 'text-success';
        }

        return (
            <div className={`${className} ${styleClass}`}>
                {!isValid ? <i className="ri-close-circle-fill"></i> : <i className="ri-checkbox-circle-fill"></i>}
                <small className="ms-2">{text}</small>
            </div>
        );
    };

    return (
        <>
            <MlxToast
                data-testid="toast"
                message={toastMessage.message}
                onClose={() => setToastMessage(defaultToastData)}
                show={toastMessage.show}
                variant={toastMessage.variant}
            />
            <MlxSideBar
                visible={showSideBar}
                position="right"
                onHide={() => onSidebarClose()}
                content={({ hide }) => (
                    <>
                        <div className="mlx-sidebar-header">
                            <span className="header-title">{isVenueUserEdit ? translatedText.EDIT_STAFF : translatedText.ADD_STAFF}</span>
                            <MlxButton
                                rounded
                                text
                                size="large"
                                icon="ri-close-fill"
                                onClick={(event) => hide(event)}
                                className="text-dark"
                            />
                        </div>

                        <form onSubmit={formik.handleSubmit} className="mlx-sidebar-body add-staff-form">
                            <MlxImageUpload
                                mode="basic"
                                image={profileImage}
                                setImage={setProfileImage}
                                validateImageDimension={IMAGE_TYPE['PROFILE_IMAGE']}
                                cropImage={true}
                                uploadType="profile"
                            />

                            <div className="mb-5 d-flex flex-column username-field">
                                <label className="mb-1" htmlFor="username">
                                    {translatedText.USERNAME} <span className="text-danger">*</span>
                                </label>
                                <InputText
                                    id="username"
                                    name="loginUserName"
                                    className={`custom-input-text p-inputtext-sm ${formik.touched.loginUserName && formik.errors.loginUserName ? 'border-danger' : ''}`}
                                    value={formik.values.loginUserName}
                                    onChange={(event) => {
                                        const value = event.target.value.trim().toLowerCase();
                                        formik.setFieldValue('loginUserName', value);
                                    }}
                                    onBlur={formik.handleBlur}
                                    autoComplete="off"
                                    disabled={isVenueUserEdit}
                                />
                                {formik.touched.loginUserName && formik.errors.loginUserName && (
                                    <div className="text-danger">{formik.errors.loginUserName}</div>
                                )}
                                <div className="mt-3 d-flex flex-row align-items-center">
                                    <Checkbox
                                        inputId="send-password-link"
                                        name="send-password-link"
                                        checked={sendPasswordLink}
                                        onChange={(e) => {
                                            setSendPasswordLink(e.checked ? true : false);
                                            if (!e.checked) {
                                                formik.setFieldValue('password', '');
                                            }
                                            formik.setFieldValue('sendPasswordCreation', e.checked);
                                        }}
                                    />
                                    <label className="ms-2" htmlFor="send-password-link">
                                        {translatedText.SEND_PASSWORD_LINK}
                                    </label>
                                </div>
                                {!isVenueUserEdit && !sendPasswordLink && (
                                    <>
                                        <label className="mb-1 mt-3" htmlFor="password">
                                            {translatedText.PASSWORD} <span className="text-danger">*</span>
                                        </label>
                                        <InputText
                                            id="password"
                                            name="password"
                                            className={`custom-input-text p-inputtext-sm ${formik.touched.password && formik.errors.password ? 'border-danger' : ''}`}
                                            value={formik.values.password}
                                            onChange={(event) => {
                                                formik.setFieldTouched('password', true);
                                                formik.setFieldValue('password', event.target.value, true);
                                            }}
                                            onBlur={formik.handleBlur}
                                            autoComplete="off"
                                        />
                                        <div className="w-100 mt-3 position-relative d-flex align-items-center">
                                            <div className="row">
                                                <PasswordRequirement
                                                    className="col-md-6 d-flex align-items-center"
                                                    text={translatedText.PASSWORD_CHECK_LOWERCASE}
                                                    isValid={passwordCriteria.lowercase}
                                                />
                                                <PasswordRequirement
                                                    className="col-md-6 d-flex align-items-center"
                                                    text={translatedText.PASSWORD_CHECK_UPPERCASE}
                                                    isValid={passwordCriteria.uppercase}
                                                />
                                                <PasswordRequirement
                                                    className="col-md-6 d-flex align-items-center"
                                                    text={translatedText.PASSWORD_CHECK_NUMBER}
                                                    isValid={passwordCriteria.number}
                                                />
                                                <PasswordRequirement
                                                    className="col-md-6 d-flex align-items-center"
                                                    text={translatedText.PASSWORD_CHECK_MIN}
                                                    isValid={passwordCriteria.length}
                                                />
                                                <PasswordRequirement
                                                    className="col-md-12 d-flex align-items-center"
                                                    text={translatedText.PASSWORD_CHECK_SPECIAL}
                                                    isValid={passwordCriteria.special}
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="mb-3 d-flex flex-column">
                                <label className="mb-1" htmlFor="displayName">
                                    {translatedText.DISPLAY_NAME} <span className="text-danger">*</span>
                                </label>
                                <InputText
                                    id="displayName"
                                    name="preferredName"
                                    className={`custom-input-text p-inputtext-sm ${formik.touched.preferredName && formik.errors.preferredName ? 'border-danger' : ''}`}
                                    value={formik.values.preferredName}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    autoComplete="off"
                                />
                                {formik.touched.preferredName && formik.errors.preferredName && (
                                    <div className="text-danger">{formik.errors.preferredName}</div>
                                )}
                            </div>

                            <div className="row">
                                <div className="col-md-6 mb-3 d-flex flex-column">
                                    <label className="mb-1" htmlFor="firstName">
                                        {translatedText.FIRST_NAME} <span className="text-danger">*</span>
                                    </label>
                                    <InputText
                                        id="firstName"
                                        name="firstName"
                                        className={`custom-input-text p-inputtext-sm ${formik.touched.firstName && formik.errors.firstName ? 'border-danger' : ''}`}
                                        value={formik.values.firstName}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        autoComplete="off"
                                    />
                                    {formik.touched.firstName && formik.errors.firstName && (
                                        <small className="text-danger">{formik.errors.firstName}</small>
                                    )}
                                </div>
                                <div className="col-md-6 mb-3 d-flex flex-column">
                                    <label className="mb-1" htmlFor="lastName">
                                        {translatedText.LAST_NAME} <span className="text-danger">*</span>
                                    </label>
                                    <InputText
                                        id="lastName"
                                        name="lastName"
                                        className={`custom-input-text p-inputtext-sm ${formik.touched.lastName && formik.errors.lastName ? 'border-danger' : ''}`}
                                        value={formik.values.lastName}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        autoComplete="off"
                                    />
                                    {formik.touched.lastName && formik.errors.lastName && (
                                        <small className="text-danger">{formik.errors.lastName}</small>
                                    )}
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6 mb-3 d-flex flex-column">
                                    <label className="mb-1" htmlFor="email">
                                        {translatedText.EMAIL} <span className="text-danger">*</span>
                                    </label>
                                    <InputText
                                        id="email"
                                        name="email"
                                        className={`custom-input-text p-inputtext-sm ${formik.touched.email && formik.errors.email ? 'border-danger' : ''}`}
                                        value={formik.values.email}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        autoComplete="off"
                                        disabled={isVenueUserEdit}
                                    />
                                    {formik.touched.email && formik.errors.email && (
                                        <small className="text-danger">{formik.errors.email}</small>
                                    )}
                                </div>
                                <div className="col-md-6 mb-3 d-flex flex-column">
                                    <label className="mb-1" htmlFor="mobile">
                                        {translatedText.PHONE}
                                    </label>
                                    <MlxCountryPhoneInput
                                        id="mobile"
                                        defaultCountry="US"
                                        value={formik.values.phoneNumber}
                                        className={`custom-input-text ${formik.touched.phoneNumber && formik.errors.phoneNumber ? 'border-danger' : ''}`}
                                        onChange={(value) => {
                                            formik.setFieldValue('phoneNumber', value || '');
                                        }}
                                        onBlur={() => {
                                            formik.setFieldTouched('phoneNumber', true);
                                        }}
                                        autoComplete="off"
                                    />
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6 mb-3 d-flex flex-column">
                                    <label className="mb-1" htmlFor="hireDate">
                                        {translatedText.HIRE_DATE}
                                    </label>
                                    <Calendar
                                        id="hireDate"
                                        name="hireDate"
                                        className="admin-staff-hiredate"
                                        value={formik.values.hireDate ? new Date(formik.values.hireDate) : null}
                                        onChange={(e) => formik.setFieldValue('hireDate', dayjs(e.value).format('YYYY-MM-DD'))}
                                        dateFormat="yy-mm-dd"
                                    />
                                    {formik.touched.hireDate && formik.errors.hireDate && (
                                        <small className="text-danger">{formik.errors.hireDate}</small>
                                    )}
                                </div>
                                <div className="col-md-6 mb-3 d-flex flex-column">
                                    <label className="mb-1" htmlFor="permissionGroup">
                                        {translatedText.PERMISSION_GROUP}
                                    </label>
                                    <MlxDropDown
                                        dropDownSize="small"
                                        options={venuePermissionGroups}
                                        loading={venuePermissionGroupsLoading}
                                        optionLabel="permissionRoleName"
                                        optionValue="id"
                                        value={formik.values.staffPermissionRoles}
                                        onChange={(e) => {
                                            formik.setFieldValue('staffPermissionRoles', e.value);
                                        }}
                                    />
                                    {formik.touched.staffPermissionRoles && formik.errors.staffPermissionRoles && (
                                        <small className="text-danger">{formik.errors.staffPermissionRoles}</small>
                                    )}
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6 mb-3 d-flex flex-column">
                                    <label className="mb-1" htmlFor="posNumber">
                                        {translatedText.POS}
                                    </label>
                                    <InputText
                                        id="posNumber"
                                        name="posNumber"
                                        className={`custom-input-text p-inputtext-sm ${formik.touched.posNumber && formik.errors.posNumber ? 'border-danger' : ''}`}
                                        value={formik.values.posNumber}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        autoComplete="off"
                                    />
                                    {formik.touched.posNumber && formik.errors.posNumber && (
                                        <small className="text-danger">{formik.errors.posNumber}</small>
                                    )}
                                </div>
                                <div className="col-md-6 mb-3 d-flex flex-column">
                                    <label className="mb-1" htmlFor="roles">
                                        {translatedText.ROLES}
                                    </label>
                                    {/* <MlxDropDown
                                        dropDownSize="small"
                                        options={venueJobRoles}
                                        optionLabel="jobRoleName"
                                        optionValue="id"
                                        value={formik.values.jobRole}
                                        onChange={(e) => {
                                            formik.setFieldValue('jobRole', e.value);
                                        }}
                                        loading={venueJobRolesLoading}
                                    /> */}
                                    <MlxMultiSelect
                                        inputId="roles"
                                        value={formik.values.jobRoles}
                                        options={venueJobRoles}
                                        optionLabel="jobRoleName"
                                        optionValue="id"
                                        onChange={(e) => formik.setFieldValue('jobRoles', e.value)}
                                        className="w-100"
                                        placeholder="Select Roles"
                                        display="chip"
                                        dropDownSize="small"
                                        loading={venueJobRolesLoading}
                                    />
                                    {formik.touched.jobRoles && formik.errors.jobRoles && (
                                        <small className="text-danger">{formik.errors.jobRoles}</small>
                                    )}
                                </div>
                            </div>
                            {/* <AddUserVenueForm
                                venueOptions={venueOptions}
                                addVenueFormData={addVenueFormData}
                                setAddVenueFormData={setAddVenueFormData}
                                addNewVenueDetails={addNewVenueDetails}
                                isVenueEdit={isVenueEdit}
                                setIsVenueEdit={setIsVenueEdit}
                                selectedVenues={formik.values.venues}
                                handleVenueEdit={handleVenueEdit}
                                deleteVenueDetails={deleteVenueDetails}
                                setAddVenueFormInitialData={setAddVenueFormInitialData}
                            /> */}
                        </form>

                        <div className="mlx-sidebar-footer">
                            <MlxButton
                                label={translatedText.CANCEL}
                                onClick={() => {
                                    onSidebarClose();
                                }}
                                icon="ri-close-large-line"
                                iconPos="right"
                                size="small"
                                outlined
                                type="button"
                            />
                            <MlxButton
                                className="ms-2"
                                label={isVenueUserEdit ? translatedText.UPDATE : translatedText.SAVE}
                                onClick={() => {
                                    //call onsubmit in formik
                                    formik.handleSubmit();
                                }}
                                icon="ri-check-line"
                                iconPos="right"
                                size="small"
                                type="button"
                                loading={addOrgStaffMutation.isLoading}
                                disabled={addOrgStaffMutation.isLoading}
                            />
                        </div>
                    </>
                )}
            />
        </>
    );
};

export default AddVenueUserSideBar;
